// Wizard steps: Apple Developer ucet + Texty pro App Store.
//
// Step 4 hosts the EXISTING honest S21 checklist (AppTrack passes it in as a
// node - the transition chain stays untouched) plus the reference wizard
// fields (Team ID, App Store Connect email). ownerConfirmedEnrollment is not
// a second ask: it auto-saves once the S21 checklist has really been
// submitted, so completeness mirrors reality.
import { useEffect, useRef, useState } from "react";
import type { AppAppleCopyPayload, AppAppleDeveloperPayload } from "../../../shared/app-wizard";
import { APP_TEXT_LIMITS } from "../../../shared/app-wizard";
import { appWizardLocales } from "../../../shared/app-wizard";
import {
  CharCount,
  FieldBlock,
  LocaleTextRows,
  SaveBadge,
  useStepSaver,
  type StepProps
} from "./wizard-shared";

const EMPTY_APPLE_DEV: AppAppleDeveloperPayload = {
  ownerConfirmedEnrollment: false,
  teamId: "",
  appStoreConnectEmail: ""
};

export function StepAppleDeveloper(props: StepProps) {
  const saved = props.data.wizard.steps.apple_developer;
  const [draft, setDraft] = useState<AppAppleDeveloperPayload>(saved ?? EMPTY_APPLE_DEV);
  const saver = useStepSaver("apple_developer", props.save, props.readOnly);
  const autoConfirmed = useRef(false);

  const update = (next: AppAppleDeveloperPayload) => {
    setDraft(next);
    saver.queue(next as unknown as Record<string, unknown>);
  };

  // The S21 checklist went through (now or in the past): record the
  // confirmation into the wizard payload once, without asking again.
  useEffect(() => {
    if (!props.appleConfirmed || draft.ownerConfirmedEnrollment || autoConfirmed.current || props.readOnly) return;
    autoConfirmed.current = true;
    update({ ...draft, ownerConfirmedEnrollment: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.appleConfirmed]);

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.appleDev.title")}</h2>
        <p>{props.t("appWizard.appleDev.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>

      {props.appleConfirmed ? (
        <p className="section-action-note success-note" role="status">{props.t("appWizard.appleDev.confirmed")}</p>
      ) : (
        <details className="appwiz-guide">
          <summary>{props.t("appGuide.title")}</summary>
          <ol className="app-guide-steps">
            {["step1", "step2", "step3", "step4", "step5"].map((key, index) => (
              <li key={key}><span className="step-num step-number-token" aria-hidden="true">{index + 1}</span><p>{props.t(`appGuide.${key}`)}</p></li>
            ))}
          </ol>
        </details>
      )}

      {/* The honest S21 confirmation chain, unchanged (AppTrack owns it). */}
      {props.appleChecklist}

      <FieldBlock label={props.t("appWizard.appleDev.teamIdLabel")} hint={props.t("appWizard.appleDev.teamIdHint")}>
        <span className="appwiz-locale-input appwiz-name-input">
          <input
            type="text"
            value={draft.teamId}
            maxLength={APP_TEXT_LIMITS.teamId}
            readOnly={props.readOnly}
            placeholder={props.t("appWizard.appleDev.teamIdPlaceholder")}
            aria-label={props.t("appWizard.appleDev.teamIdLabel")}
            onChange={(event) => update({ ...draft, teamId: event.target.value })}
          />
          <CharCount value={draft.teamId} max={APP_TEXT_LIMITS.teamId} />
        </span>
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.appleDev.emailLabel")} hint={props.t("appWizard.appleDev.emailHint")}>
        <input
          className="appwiz-plain-input"
          type="email"
          value={draft.appStoreConnectEmail}
          readOnly={props.readOnly}
          placeholder={props.t("appWizard.appleDev.emailPlaceholder")}
          aria-label={props.t("appWizard.appleDev.emailLabel")}
          onChange={(event) => update({ ...draft, appStoreConnectEmail: event.target.value })}
        />
      </FieldBlock>
    </div>
  );
}

const EMPTY_APPLE_COPY: AppAppleCopyPayload = {
  subtitle: {},
  promotionalText: {},
  keywords: {},
  description: {}
};

export function StepAppleCopy(props: StepProps) {
  const [draft, setDraft] = useState<AppAppleCopyPayload>(props.data.wizard.steps.apple_copy ?? EMPTY_APPLE_COPY);
  const saver = useStepSaver("apple_copy", props.save, props.readOnly);
  const locales = appWizardLocales(props.data.wizard);

  const update = (next: AppAppleCopyPayload) => {
    setDraft(next);
    saver.queue(next as unknown as Record<string, unknown>);
  };

  if (locales.length === 0) {
    return (
      <div className="appwiz-step">
        <header className="appwiz-step-heading"><h2>{props.t("appWizard.appleCopy.title")}</h2></header>
        <p className="appwiz-need-basics">{props.t("appWizard.copy.needLanguages")}</p>
        <button className="secondary-button compact-button" type="button" onClick={() => props.onOpenStep("basics")}>
          {props.t("appWizard.copy.goBasics")}
        </button>
      </div>
    );
  }

  return (
    <div className="appwiz-step">
      <header className="appwiz-step-heading">
        <h2>{props.t("appWizard.appleCopy.title")}</h2>
        <p>{props.t("appWizard.appleCopy.copy")}</p>
        <SaveBadge state={saver.state} t={props.t} />
      </header>

      <FieldBlock label={props.t("appWizard.appleCopy.subtitleLabel")} hint={props.t("appWizard.appleCopy.subtitleHint")}>
        <LocaleTextRows
          locales={locales} value={draft.subtitle} max={APP_TEXT_LIMITS.subtitle}
          csrfToken={props.csrfToken} readOnly={props.readOnly}
          ariaLabel={props.t("appWizard.appleCopy.subtitleLabel")} t={props.t}
          onChange={(subtitle) => update({ ...draft, subtitle })}
        />
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.appleCopy.descriptionLabel")} hint={props.t("appWizard.appleCopy.descriptionHint")}>
        <LocaleTextRows
          locales={locales} value={draft.description} max={APP_TEXT_LIMITS.description}
          voice csrfToken={props.csrfToken} readOnly={props.readOnly}
          ariaLabel={props.t("appWizard.appleCopy.descriptionLabel")} t={props.t}
          onChange={(description) => update({ ...draft, description })}
        />
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.appleCopy.keywordsLabel")} hint={props.t("appWizard.appleCopy.keywordsHint")}>
        <LocaleTextRows
          locales={locales} value={draft.keywords} max={APP_TEXT_LIMITS.keywords}
          csrfToken={props.csrfToken} readOnly={props.readOnly}
          ariaLabel={props.t("appWizard.appleCopy.keywordsLabel")} t={props.t}
          onChange={(keywords) => update({ ...draft, keywords })}
        />
      </FieldBlock>

      <FieldBlock label={props.t("appWizard.appleCopy.promoLabel")} hint={props.t("appWizard.appleCopy.promoHint")}>
        <LocaleTextRows
          locales={locales} value={draft.promotionalText} max={APP_TEXT_LIMITS.promotionalText}
          multiline csrfToken={props.csrfToken} readOnly={props.readOnly}
          ariaLabel={props.t("appWizard.appleCopy.promoLabel")} t={props.t}
          onChange={(promotionalText) => update({ ...draft, promotionalText })}
        />
      </FieldBlock>
    </div>
  );
}
